export interface FeatureFlag {
  key: string
  enabled: boolean
  description?: string
  metadata?: Record<string, unknown>
}

export interface FeatureFlagsProvider {
  getFlag(key: string): boolean
  getAllFlags(): Record<string, boolean>
  setFlag(key: string, enabled: boolean): void
  subscribe(listener: (flags: Record<string, boolean>) => void): () => void
}

export class LocalStorageFeatureFlagsProvider implements FeatureFlagsProvider {
  private flags: Map<string, boolean> = new Map()
  private listeners: Set<(flags: Record<string, boolean>) => void> = new Set()
  private storageKey = 'tradovate_feature_flags'

  constructor(defaultFlags: Record<string, boolean> = {}) {
    this.loadFlags(defaultFlags)
  }

  private loadFlags(defaultFlags: Record<string, boolean>): void {
    try {
      const stored = localStorage.getItem(this.storageKey)
      const parsedFlags = stored ? JSON.parse(stored) : {}

      Object.entries(defaultFlags).forEach(([key, value]) => {
        this.flags.set(key, parsedFlags[key] ?? value)
      })
      Object.entries(parsedFlags).forEach(([key, value]) => {
        if (!this.flags.has(key)) {
          this.flags.set(key, Boolean(value))
        }
      })
    } catch (error) {
      console.error('Failed to load feature flags from localStorage:', error)
      Object.entries(defaultFlags).forEach(([key, value]) => {
        this.flags.set(key, value)
      })
    }
  }

  private saveFlags(): void {
    try {
      const flagsObject = Object.fromEntries(this.flags)
      localStorage.setItem(this.storageKey, JSON.stringify(flagsObject))
    } catch (error) {
      console.error('Failed to save feature flags to localStorage:', error)
    }
  }

  getFlag(key: string): boolean {
    return this.flags.get(key) ?? false
  }

  getAllFlags(): Record<string, boolean> {
    return Object.fromEntries(this.flags)
  }

  setFlag(key: string, enabled: boolean): void {
    this.flags.set(key, enabled)
    this.saveFlags()
    this.notifyListeners()
  }

  subscribe(listener: (flags: Record<string, boolean>) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notifyListeners(): void {
    const flags = this.getAllFlags()
    this.listeners.forEach(listener => listener(flags))
  }
}

export class FeatureFlags {
  // Using any to keep compatibility with existing code that pokes into provider (hooks)
  static provider: FeatureFlagsProvider = new LocalStorageFeatureFlagsProvider()

  static setProvider(provider: FeatureFlagsProvider): void {
    this.provider = provider
  }

  static isEnabled(key: string): boolean {
    return this.provider.getFlag(key)
  }

  static enable(key: string): void {
    this.provider.setFlag(key, true)
  }

  static disable(key: string): void {
    this.provider.setFlag(key, false)
  }

  static getAllFlags(): Record<string, boolean> {
    return this.provider.getAllFlags()
  }

  static subscribe(listener: (flags: Record<string, boolean>) => void): () => void {
    return this.provider.subscribe(listener)
  }
}

